/**
 * WAV File Validation Utilities
 * 
 * Validates WAV files for:
 * - Correct file format (.wav)
 * - 24-bit depth
 * - 48000Hz sample rate
 * - Filename: separators ignored, one typo allowed, dynamic marks (p/f/ff/...) exact
 * - Minimum duration requirement
 * - Duplicate file detection (via content hash)
 */

/**
 * Reads WAV file header and extracts audio properties
 * @param {File} file - The file to analyze
 * @returns {Promise<{sampleRate: number, bitDepth: number, channels: number, durationSeconds: number}>}
 */
async function readWavHeader(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const buffer = e.target.result;
      const view = new DataView(buffer);
      
      // Check RIFF header
      const riff = String.fromCharCode(
        view.getUint8(0),
        view.getUint8(1),
        view.getUint8(2),
        view.getUint8(3)
      );
      
      if (riff !== 'RIFF') {
        reject(new Error('Not a valid WAV file (missing RIFF header)'));
        return;
      }
      
      // Check WAVE format
      const wave = String.fromCharCode(
        view.getUint8(8),
        view.getUint8(9),
        view.getUint8(10),
        view.getUint8(11)
      );
      
      if (wave !== 'WAVE') {
        reject(new Error('Not a valid WAV file (missing WAVE format)'));
        return;
      }
      
      // Find fmt and data chunks
      let offset = 12;
      let fmtFound = false;
      let channels = 0;
      let sampleRate = 0;
      let bitDepth = 0;
      let dataSize = 0;
      
      while (offset < buffer.byteLength - 8) {
        const chunkId = String.fromCharCode(
          view.getUint8(offset),
          view.getUint8(offset + 1),
          view.getUint8(offset + 2),
          view.getUint8(offset + 3)
        );
        const chunkSize = view.getUint32(offset + 4, true);
        
        if (chunkId === 'fmt ') {
          // Found the format chunk
          channels = view.getUint16(offset + 10, true);
          sampleRate = view.getUint32(offset + 12, true);
          bitDepth = view.getUint16(offset + 22, true);
          fmtFound = true;
        } else if (chunkId === 'data') {
          // Found the data chunk - size tells us audio length
          dataSize = chunkSize;
        }
        
        // Move to next chunk (but don't skip past buffer for data chunk)
        // For non-data chunks, skip their content; for data chunk, we already have the size
        if (chunkId === 'data') {
          break; // We found data chunk, no need to continue
        }
        offset += 8 + chunkSize;
        // Ensure even alignment
        if (chunkSize % 2 !== 0) offset += 1;
      }
      
      if (!fmtFound) {
        reject(new Error('Could not find format chunk in WAV file'));
        return;
      }
      
      // Calculate duration
      const bytesPerSample = bitDepth / 8;
      const bytesPerSecond = sampleRate * channels * bytesPerSample;
      let durationSeconds = 0;
      
      if (dataSize > 0 && bytesPerSecond > 0) {
        // Use data chunk size if we found it
        durationSeconds = dataSize / bytesPerSecond;
      } else if (bytesPerSecond > 0) {
        // Fallback: estimate from file size minus typical header overhead (~44 bytes)
        const estimatedDataSize = file.size - 44;
        durationSeconds = estimatedDataSize / bytesPerSecond;
      }
      
      resolve({ sampleRate, bitDepth, channels, durationSeconds });
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    // Read first 64KB - enough to find data chunk even with metadata
    const slice = file.slice(0, 65536);
    reader.readAsArrayBuffer(slice);
  });
}

/**
 * Letter dynamics must match exactly. Fuzzy matching applies only to the
 * other words, so "f" cannot pass as a one-character typo for "p".
 * Longer marks are listed first so "ff" is not read as "f".
 */
const DYNAMIC_MARKINGS = ['ppp', 'fff', 'sfz', 'mf', 'mp', 'fp', 'ff', 'pp', 'fz', 'sf', 'f', 'p'];
const DYNAMIC_MARKING_SET = new Set(DYNAMIC_MARKINGS);

/** Typos allowed across the non-dynamic letters. Separators are free. */
const FILENAME_FUZZY_BUDGET = 1;

function filenameStem(filename) {
  return String(filename || '').replace(/\.wav$/i, '');
}

function filenameTokens(filename) {
  return filenameStem(filename)
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

function filenameLetters(filename) {
  return filenameStem(filename).toLowerCase().replace(/[^a-z0-9]/g, '');
}

function isDynamicMarking(token) {
  return DYNAMIC_MARKING_SET.has(token);
}

/**
 * Split the expected name into text runs and dynamic marks, in order.
 * "Tongued_Legato_-_p_-_soft" -> ["tonguedlegato", "p", "soft"]
 */
function expectedFilenameSegments(filename) {
  const segments = [];
  let text = '';

  const flushText = () => {
    if (!text) return;
    segments.push({ type: 'text', value: text });
    text = '';
  };

  for (const token of filenameTokens(filename)) {
    if (isDynamicMarking(token)) {
      flushText();
      segments.push({ type: 'dynamic', value: token });
    } else {
      text += token;
    }
  }
  flushText();
  return segments;
}

function longestDynamicAt(letters, index) {
  for (const marking of DYNAMIC_MARKINGS) {
    if (letters.startsWith(marking, index)) return marking;
  }
  return null;
}

/** True when this "f" is really the start or the tail of "ff" / "fff" / "fp". */
function dynamicBoundaryIsExact(letters, index, expected) {
  if (longestDynamicAt(letters, index) !== expected) return false;

  for (const marking of DYNAMIC_MARKINGS) {
    if (marking.length <= expected.length || !marking.endsWith(expected)) continue;
    const start = index - (marking.length - expected.length);
    if (start >= 0 && letters.startsWith(marking, start)) return false;
  }
  return true;
}

function filenameMismatch(expected) {
  return {
    matches: false,
    needsConfirmation: false,
    reason: `Filename must be "${expected}" — are you sure you chose the right file?`
  };
}

/**
 * Match letters against expected segments. Dynamic marks are exact, including
 * ff versus f. Text may differ by up to the remaining typo budget.
 * Separators are already gone.
 */
function filenameSegmentsMatch(letters, segments, segmentIndex, budget) {
  if (segmentIndex === segments.length) {
    return letters.length <= budget;
  }

  const segment = segments[segmentIndex];

  if (segment.type === 'dynamic') {
    if (!dynamicBoundaryIsExact(letters, 0, segment.value)) return false;
    return filenameSegmentsMatch(
      letters.slice(segment.value.length),
      segments,
      segmentIndex + 1,
      budget
    );
  }

  const next = segments[segmentIndex + 1];
  const minLen = Math.max(0, segment.value.length - budget);
  const maxLen = Math.min(letters.length, segment.value.length + budget);
  for (let len = minLen; len <= maxLen; len++) {
    const dist = levenshteinDistance(letters.slice(0, len), segment.value);
    if (dist > budget) continue;

    if (next && next.type === 'dynamic') {
      if (!dynamicBoundaryIsExact(letters, len, next.value)) continue;
      const rest = letters.slice(len + next.value.length);
      if (filenameSegmentsMatch(rest, segments, segmentIndex + 2, budget - dist)) {
        return true;
      }
      continue;
    }

    if (filenameSegmentsMatch(letters.slice(len), segments, segmentIndex + 1, budget - dist)) {
      return true;
    }
  }
  return false;
}

/**
 * Calculates Levenshtein distance between two strings
 * @param {string} a - First string
 * @param {string} b - Second string
 * @returns {number} - Edit distance
 */
function levenshteinDistance(a, b) {
  const matrix = [];
  
  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }
  
  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1,     // insertion
          matrix[i - 1][j] + 1      // deletion
        );
      }
    }
  }
  
  return matrix[b.length][a.length];
}

/**
 * Checks whether a chosen filename belongs to this recording box.
 *
 * Separators (-, _, spaces, or none at all) are ignored. One typo is allowed
 * in the words. Dynamic marks (p, mp, mf, f, ff, pp, and the like) must match
 * exactly, so an f file cannot pass as a typo for p.
 *
 * @param {string} actual - The actual filename
 * @param {string} expected - The expected filename
 * @returns {{matches: boolean, needsConfirmation: boolean, reason: string|null}}
 */
export function checkFilename(actual, expected) {
  if (actual === expected) {
    return { matches: true, needsConfirmation: false, reason: null };
  }

  const letters = filenameLetters(actual);
  const segments = expectedFilenameSegments(expected);
  if (!letters || segments.length === 0) {
    return filenameMismatch(expected);
  }

  if (filenameSegmentsMatch(letters, segments, 0, FILENAME_FUZZY_BUDGET)) {
    return { matches: true, needsConfirmation: false, reason: null };
  }

  return filenameMismatch(expected);
}

/**
 * Formats seconds into a human-readable duration string
 * @param {number} seconds - Duration in seconds
 * @returns {string} - Formatted string like "2m 30s" or "45s"
 */
function formatDuration(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  if (mins > 0) {
    return `${mins}m ${secs}s`;
  }
  return `${secs}s`;
}

/**
 * Validates a WAV file against required specifications
 * @param {File} file - The file to validate
 * @param {string} expectedFilename - The expected filename
 * @param {number|null} minimumDurationSeconds - Minimum required duration in seconds (null to skip check)
 * @returns {Promise<{valid: boolean, errors: string[], needsConfirmation: boolean, confirmationMessage: string|null}>}
 */
export async function validateWavFile(file, expectedFilename, minimumDurationSeconds = null) {
  const errors = [];
  let needsConfirmation = false;
  let confirmationMessage = null;
  
  // 1. Check file extension
  const filename = file.name.toLowerCase();
  if (!filename.endsWith('.wav')) {
    errors.push('File must be a .wav file');
  }
  
  // 2. Check filename matches expected (with tolerance)
  const filenameCheck = checkFilename(file.name, expectedFilename);
  if (!filenameCheck.matches) {
    errors.push(filenameCheck.reason);
  } else if (filenameCheck.needsConfirmation) {
    needsConfirmation = true;
    confirmationMessage = filenameCheck.reason;
  }
  
  // 3. Check WAV header properties (only if it's a .wav file)
  if (filename.endsWith('.wav')) {
    try {
      const { sampleRate, bitDepth, durationSeconds } = await readWavHeader(file);
      
      // Check bit depth
      if (bitDepth !== 24) {
        errors.push(`File must be 24-bit (got ${bitDepth}-bit)`);
      }
      
      // Check sample rate
      if (sampleRate !== 48000) {
        errors.push(`File must be 48000Hz (got ${sampleRate}Hz)`);
      }
      
      // Check minimum duration (if specified)
      // The incoming value is already 80% of the true score duration,
      // so to enforce 50% of the original: (value / 0.8) * 0.5 = value * 0.625
      if (minimumDurationSeconds != null && minimumDurationSeconds > 0) {
        const effectiveMinimum = minimumDurationSeconds * 0.625;
        if (durationSeconds < effectiveMinimum) {
          errors.push(
            `Recording is too short (${formatDuration(durationSeconds)}). ` +
            `Minimum required: ${formatDuration(effectiveMinimum)}`
          );
        }
      }
    } catch (err) {
      errors.push(err.message);
    }
  }
  
  return {
    valid: errors.length === 0,
    errors,
    needsConfirmation,
    confirmationMessage
  };
}

/**
 * Formats validation errors into a user-friendly message
 * @param {string[]} errors - Array of error messages
 * @returns {string}
 */
export function formatValidationErrors(errors) {
  if (errors.length === 1) {
    return errors[0];
  }
  return errors.join(' • ');
}

/**
 * Generates a hash of file contents for duplicate detection.
 * Uses the Web Crypto API (SHA-256) for reliable content comparison.
 * @param {File} file - The file to hash
 * @returns {Promise<string>} - Hex string hash of the file contents
 */
export async function generateFileHash(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = async (e) => {
      try {
        const buffer = e.target.result;
        const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        resolve(hashHex);
      } catch (err) {
        reject(new Error('Failed to generate file hash'));
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file for hashing'));
    };
    
    reader.readAsArrayBuffer(file);
  });
}
